import type { QueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';

/**
 * Approving and withdrawing a refund, shared by the invoice page and the
 * booking page: one API, `pos.refund_approve`. It lives here, not in a
 * feature, because features never import each other (CLAUDE.md §4).
 * Money is a decimal STRING on this boundary.
 */

/** What a refund may be paid out as. */
export type PayoutMethod = 'CASH' | 'CARD' | 'BANK_TRANSFER';

export type RefundStatus = 'APPROVED' | 'PAID' | 'CANCELLED';

export interface Refund {
  uid: string;
  /** Null until it is paid out. */
  refundNo: string | null;
  status: RefundStatus;
  amount: string;
  method: string;
  reason: string;
  reference: string | null;
  source: { type: 'BOOKING' | 'INVOICE'; uid: string; no: string };
  customerName: string | null;
  approvedByName: string;
  approvedAt: string;
  paidByName: string | null;
  paidAt: string | null;
  /** The user may pay it out now: not the approver, and it is still waiting. */
  canPay: boolean;
  /** The user may cancel it: holds the approval permission and it is still waiting. */
  canCancel: boolean;
  printable: boolean;
}

/** What a booking or a voided invoice holds, and how much may be handed back. */
export interface CreditView {
  held: string;
  forfeited: string;
  refunded: string;
  awaitingPayout: string;
  notRefundable: string;
  refundable: string;
}

export function approveRefund(input: {
  bookingUid?: string;
  invoiceUid?: string;
  amount: string;
  method: PayoutMethod;
  reason: string;
}): Promise<Refund> {
  return api<Refund>('/pos/refunds', { method: 'POST', body: input });
}

export function cancelRefund(uid: string, reason: string): Promise<Refund> {
  return api<Refund>(`/pos/refunds/${uid}/transitions/cancel`, { method: 'POST', body: { reason } });
}

/**
 * After a refund is approved or withdrawn: the invoice or booking it came from,
 * the customer's credit, and the Till's pay-out list. Prefixes, not imports.
 */
export function invalidateAfterRefund(queryClient: QueryClient): void {
  for (const prefix of [
    'invoice',
    'invoices',
    'booking',
    'booking-money',
    'customer-credit',
    'customer',
    'refunds-to-pay',
    'till-session',
  ]) {
    void queryClient.invalidateQueries({ queryKey: [prefix] });
  }
}
